import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Payroll from "@/pages/Payroll";
import AnnualReport from "@/pages/AnnualReport";
import Budget from "@/pages/Budget";
import TipsDistribution from "@/pages/TipsDistribution";
import Payslips from "@/pages/Payslips";
import ScheduleBuilder from "@/pages/ScheduleBuilder";
import Leave from "@/pages/Leave";
import PLBudget from "@/pages/PLBudget";
import PLReport from "@/pages/PLReport";
import Invoices from "@/pages/Invoices";
import CashFlow from "@/pages/CashFlow";
import Bookings from "@/pages/Bookings";
import Expenses from "@/pages/Expenses";
import Imports from "@/pages/Imports";
import DayRoster from "@/pages/DayRoster";
import Income from "@/pages/Income";
import Events from "@/pages/Events";
import IncomeBudget from "@/pages/IncomeBudget";
import ExpenseBudget from "@/pages/ExpenseBudget";
import Assistant from "@/pages/Assistant";
import SchedulePrint from "@/pages/SchedulePrint";
import Users from "@/pages/Users";
import Trash from "@/pages/Trash";
import Overtime from "@/pages/Overtime";
import PLVariance from "@/pages/PLVariance";
import SupplierExpenses from "@/pages/SupplierExpenses";
import Recipes from "@/pages/Recipes";
import Ingredients from "@/pages/Ingredients";
import Settings from "@/pages/Settings";
import Vendors from "@/pages/Vendors";
import Catalog from "@/pages/Catalog";
import PriceCompare from "@/pages/PriceCompare";
import Bidding from "@/pages/Bidding";
import OrderList from "@/pages/OrderList";
import Integrations from "@/pages/Integrations";
import Reservations from "@/pages/Reservations";
import VillaPlan from "@/pages/VillaPlan";
import Pace from "@/pages/Pace";
import PurchaseOrders from "@/pages/PurchaseOrders";
import SupplierPortal from "@/pages/SupplierPortal";
import StockCount from "@/pages/StockCount";
import StaffStockCount from "@/pages/StaffStockCount";
import BusinessStats from "@/pages/BusinessStats";
import PurchasePivot from "@/pages/PurchasePivot";
import FinanceCategories from "@/pages/FinanceCategories";
import MyMaintenance from "@/pages/MyMaintenance";
import NewMaintenance from "@/pages/NewMaintenance";
import MenuHub from "@/pages/MenuHub";
import Ledger from "@/pages/Ledger";
import RecipeBulkEdit from "@/pages/RecipeBulkEdit";
import MaintenanceTasks from "@/pages/MaintenanceTasks";
import AuditLog from "@/pages/AuditLog";
import AuthCallback from "@/pages/AuthCallback";
import HRSettings from "@/pages/HRSettings";
import FieldOpsSettings from "@/pages/FieldOpsSettings";
import Readings from "@/pages/Readings";
import Permissions from "@/pages/Permissions";
import FieldOps from "@/pages/FieldOps";
import FieldOpsIssues from "@/pages/FieldOpsIssues";
import FieldOpsKPIs from "@/pages/FieldOpsKPIs";
import ScheduleRequests from "@/pages/ScheduleRequests";
import MyAvailability from "@/pages/MyAvailability";
import KioskClock from "@/pages/KioskClock";
import Timesheets from "@/pages/Timesheets";

const page = (node) => (
  <ProtectedRoute>
    <Layout>{node}</Layout>
  </ProtectedRoute>
);


function AppRoutes() {
  const location = useLocation();
  // Intercept the Google OAuth return (…/#session_id=…) BEFORE any ProtectedRoute runs.
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/kiosk" element={<KioskClock standalone />} />
            <Route path="/" element={page(<Dashboard />)} />
            <Route path="/employees" element={page(<Employees />)} />
            <Route path="/payroll" element={page(<Payroll />)} />
            <Route path="/report" element={page(<AnnualReport />)} />
            <Route path="/budget" element={page(<Budget />)} />
            <Route path="/tips" element={page(<TipsDistribution />)} />
            <Route path="/payslips" element={page(<Payslips />)} />
            <Route path="/timeclock" element={<Navigate to="/team/timeclock" replace />} />
            <Route path="/schedule" element={page(<ScheduleBuilder />)} />
            <Route path="/schedule-legacy" element={<Navigate to="/schedule" replace />} />
            <Route path="/attendance" element={<Navigate to="/team/timesheets" replace />} />
            <Route path="/overtime" element={page(<Overtime />)} />
            <Route path="/leave" element={page(<Leave />)} />
            <Route path="/pl-budget" element={page(<PLBudget />)} />
            <Route path="/pl-report" element={page(<PLReport />)} />
            <Route path="/business-stats" element={page(<BusinessStats />)} />
            <Route path="/purchase-pivot" element={page(<PurchasePivot />)} />
            <Route path="/finance-categories" element={page(<FinanceCategories />)} />
            <Route path="/pl-variance" element={page(<PLVariance />)} />
            <Route path="/supplier-expenses" element={page(<SupplierExpenses />)} />
            <Route path="/recipes" element={page(<Recipes />)} />
            <Route path="/ingredients" element={page(<Ingredients />)} />
            <Route path="/recipes-bulk" element={page(<RecipeBulkEdit />)} />
            <Route path="/stock-count" element={page(<StockCount />)} />
            <Route path="/stock-count/staff" element={page(<StaffStockCount />)} />
            <Route path="/pools" element={<Navigate to="/readings?tab=pools" replace />} />
            <Route path="/utilities" element={<Navigate to="/readings?tab=utilities" replace />} />
            <Route path="/readings" element={page(<Readings />)} />
            <Route path="/maintenance-tasks" element={page(<MaintenanceTasks />)} />
            <Route path="/maintenance/my" element={page(<MyMaintenance />)} />
            <Route path="/maintenance/new" element={page(<NewMaintenance />)} />
            <Route path="/menu" element={page(<MenuHub />)} />
            <Route path="/menu/:key" element={page(<MenuHub />)} />
            <Route path="/winter-tasks" element={<Navigate to="/maintenance-tasks?tag=winter" replace />} />
            <Route path="/vendors" element={page(<Vendors />)} />
            <Route path="/catalog" element={page(<Catalog />)} />
            <Route path="/price-compare" element={page(<PriceCompare />)} />
            <Route path="/bidding" element={page(<Bidding />)} />
            <Route path="/supply-order" element={page(<OrderList />)} />
            <Route path="/integrations" element={page(<Integrations />)} />
            <Route path="/reservations" element={page(<Reservations />)} />
            <Route path="/villa-plan" element={page(<VillaPlan />)} />
            <Route path="/pace" element={page(<Pace />)} />
            <Route path="/orders" element={page(<PurchaseOrders />)} />
            <Route path="/portal" element={
              <ProtectedRoute roles={["supplier"]}><SupplierPortal /></ProtectedRoute>
            } />
            <Route path="/settings" element={page(<Settings />)} />
            <Route path="/invoices" element={page(<Invoices />)} />
            <Route path="/cashflow" element={page(<CashFlow />)} />
            <Route path="/loans" element={page(<Bookings view="loans" />)} />
            <Route path="/bookings" element={<Navigate to="/loans" replace />} />
            <Route path="/cheques" element={page(<Bookings view="cheques" />)} />
            <Route path="/expense-budget" element={page(<ExpenseBudget />)} />
            <Route path="/expenses" element={page(<Expenses />)} />
            <Route path="/imports" element={page(<Imports />)} />
            <Route path="/ledger" element={page(<Ledger />)} />
            <Route path="/day-roster" element={page(<DayRoster />)} />
            <Route path="/income" element={page(<Income />)} />
            <Route path="/events" element={page(<Events />)} />
            <Route path="/income-budget" element={page(<IncomeBudget />)} />
            <Route path="/assistant" element={page(<Assistant />)} />
            <Route path="/schedule/print" element={page(<SchedulePrint />)} />
            <Route path="/users" element={page(<Users />)} />
            <Route path="/trash" element={page(<Trash />)} />
            <Route path="/audit-log" element={page(<AuditLog />)} />
            <Route path="/team" element={<Navigate to="/team/settings?tab=team" replace />} />
            <Route path="/team/members" element={<Navigate to="/team/settings?tab=team" replace />} />
            <Route path="/team/departments" element={<Navigate to="/team/settings?tab=depts" replace />} />
            <Route path="/team/settings" element={page(<HRSettings />)} />
            <Route path="/team/permissions" element={page(<Permissions />)} />
            <Route path="/team/requests" element={page(<ScheduleRequests />)} />
            <Route path="/team/availability" element={page(<MyAvailability />)} />
            <Route path="/team/timeclock" element={page(<KioskClock />)} />
            <Route path="/team/timesheets" element={page(<Timesheets />)} />
            <Route path="/field-ops" element={page(<FieldOps />)} />
            <Route path="/field-ops/housekeeping" element={page(<FieldOps scope="housekeeping" />)} />
            <Route path="/field-ops/maintenance" element={page(<FieldOps scope="maintenance" />)} />
            <Route path="/field-ops/units" element={<Navigate to="/field-ops/settings?tab=units" replace />} />
            <Route path="/field-ops/checklists" element={<Navigate to="/field-ops/settings?tab=checklists" replace />} />
            <Route path="/field-ops/settings" element={page(<FieldOpsSettings />)} />
            <Route path="/field-ops/issues" element={page(<FieldOpsIssues />)} />
            <Route path="/field-ops/stock" element={<Navigate to="/stock-count/staff?dept=housekeeping&mode=quick" replace />} />
            <Route path="/field-ops/kpis" element={page(<FieldOpsKPIs />)} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
